import React, { useState, useEffect } from 'react';
import { BarChart3, Clock, Zap, Database, Users, AlertTriangle } from 'lucide-react';

const PerformanceMetrics = ({ logs = [], visible = false }) => {
  const [metrics, setMetrics] = useState({
    totalLogs: 0,
    logsToday: 0,
    topUsers: [],
    topActions: [],
    severityBreakdown: {},
    avgResponseTime: 0,
    cacheHitRate: 0
  });

  useEffect(() => {
    if (!visible || logs.length === 0) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate metrics
    const logsToday = logs.filter(log => {
      const logDate = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
      return logDate >= today;
    }).length;

    // Top users
    const userCounts = {};
    logs.forEach(log => {
      userCounts[log.userEmail] = (userCounts[log.userEmail] || 0) + 1;
    });
    const topUsers = Object.entries(userCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([email, count]) => ({ email, count }));

    // Top actions
    const actionCounts = {};
    logs.forEach(log => {
      actionCounts[log.action] = (actionCounts[log.action] || 0) + 1;
    });
    const topActions = Object.entries(actionCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([action, count]) => ({ action, count }));

    // Severity breakdown
    const severityBreakdown = {};
    logs.forEach(log => {
      severityBreakdown[log.severity] = (severityBreakdown[log.severity] || 0) + 1;
    });

    setMetrics({
      totalLogs: logs.length,
      logsToday,
      topUsers,
      topActions,
      severityBreakdown,
      avgResponseTime: Math.random() * 200 + 50, // Mock data
      cacheHitRate: Math.random() * 0.3 + 0.7 // Mock data
    });
  }, [logs, visible]);

  if (!visible) return null;

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-500';
      case 'HIGH': return 'bg-orange-500';
      case 'MEDIUM': return 'bg-yellow-500';
      case 'LOW': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2" />
          Performance Metrics
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Logs */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-medium">Total Logs</p>
              <p className="text-2xl font-bold text-blue-900">{metrics.totalLogs}</p>
            </div>
            <Database className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        {/* Today's Logs */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-medium">Today's Logs</p>
              <p className="text-2xl font-bold text-green-900">{metrics.logsToday}</p>
            </div>
            <Clock className="w-8 h-8 text-green-500" />
          </div>
        </div>

        {/* Avg Response Time */}
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-600 font-medium">Avg Response</p>
              <p className="text-2xl font-bold text-yellow-900">{Math.round(metrics.avgResponseTime)}ms</p>
            </div>
            <Zap className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        {/* Cache Hit Rate */}
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-medium">Cache Hit Rate</p>
              <p className="text-2xl font-bold text-purple-900">{Math.round(metrics.cacheHitRate * 100)}%</p>
            </div>
            <Zap className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Top Users */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <Users className="w-4 h-4 mr-2" />
            Top Users
          </h4>
          <div className="space-y-2">
            {metrics.topUsers.map((user, index) => (
              <div key={user.email} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 truncate">{user.email}</span>
                <span className="text-gray-900 font-medium">{user.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Actions */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Top Actions
          </h4>
          <div className="space-y-2">
            {metrics.topActions.map((action, index) => (
              <div key={action.action} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{action.action}</span>
                <span className="text-gray-900 font-medium">{action.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Severity Breakdown */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Severity Breakdown
          </h4>
          <div className="space-y-2">
            {Object.entries(metrics.severityBreakdown).map(([severity, count]) => (
              <div key={severity} className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${getSeverityColor(severity)}`}></div>
                  <span className="text-gray-600">{severity}</span>
                </div>
                <span className="text-gray-900 font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;
